import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

export function Spinner() { return <span className="spin" aria-hidden /> }

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}
export function Button({ variant = 'default', size = 'md', loading, children, className = '', disabled, ...rest }: BtnProps) {
  const cls = ['btn', variant !== 'default' ? variant : '', size !== 'md' ? size : '', className]
    .filter(Boolean).join(' ')
  return (
    // A button that stays clickable while its request is in flight is how
    // duplicate records get created. Disabling during `loading` is the
    // first line of that defence; the idempotent id is the second.
    <button className={cls} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner /> : null}
      {children}
    </button>
  )
}

export function Field({ label, hint, error, htmlFor, children }: {
  label: string; hint?: string; error?: string; htmlFor?: string; children: ReactNode
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <span className="err">{error}</span>
        : hint ? <span className="hint">{hint}</span> : null}
    </div>
  )
}

// forwardRef so a form can focus its first field on mount -- reception
// types from a paper form and should not have to reach for the mouse.
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className = '', ...p }, ref) => <input ref={ref} className={`input ${className}`} {...p} />,
)
Input.displayName = 'Input'

export const Select = ({ className = '', ...p }: SelectHTMLAttributes<HTMLSelectElement>) =>
  <select className={`select ${className}`} {...p} />

export const Textarea = ({ className = '', ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) =>
  <textarea className={`textarea ${className}`} {...p} />

export function RoleBadge({ role, active = true }: { role: string; active?: boolean }) {
  if (!active) return <span className="badge off"><i className="dot" />Deactivated</span>
  return <span className={`badge ${role}`}>{role[0]!.toUpperCase() + role.slice(1)}</span>
}

export function Skeleton({ w = '100%', h = 14 }: { w?: string | number; h?: number }) {
  return <div className="skel" style={{ width: w, height: h }} />
}

export function SkeletonRows({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  // Matches the real table's shape so nothing jumps when data lands.
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}><Skeleton w={c === 0 ? '55%' : c === cols - 1 ? '40%' : '70%'} /></td>
          ))}
        </tr>
      ))}
    </>
  )
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="empty"><h3>{title}</h3>{children ? <p className="muted">{children}</p> : null}</div>
}
